/* ---------------------------------------------------------------------------
 * MD Fashionwear — wishlist speed layer
 *
 * THE PROBLEM THIS SOLVES
 * Opening the wishlist used to mean: navigate, wait for the page, run JS, then
 * fire one network request per saved item and wait for the slowest. The list sat
 * empty for most of a second.
 *
 * THE FIX, IN TWO PARTS
 *  1. A sessionStorage cache of trimmed product data. On open, rows render
 *     synchronously from cache with no network at all, then quietly revalidate.
 *  2. Intent prefetching. The moment a shopper hovers, touches or tabs to any
 *     wishlist link, we prefetch the page itself AND warm every saved product
 *     into the cache. By the time the click lands, both are ready.
 *
 * Cache entries are namespaced by currency + country, because /products/x.js
 * returns presentment prices. Switching market must never show stale prices.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var KEY = 'md_wl_cache_v2';
  var TTL = 10 * 60 * 1000;   // 10 minutes
  var PAGE = '/pages/page-wishlist';

  function ctx() {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    var country = (window.Shopify && window.Shopify.country) || '';
    return cur + '|' + country;
  }

  function readAll() {
    try {
      var o = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (!o || o.ctx !== ctx() || !o.items) return { ctx: ctx(), items: {} };
      return o;
    } catch (e) {
      return { ctx: ctx(), items: {} };
    }
  }

  function writeAll(store) {
    try { sessionStorage.setItem(KEY, JSON.stringify(store)); } catch (e) {}
  }

  // Keep only what the row needs. Full product JSON is 11-22KB each; this is a
  // fraction of that, so a large wishlist still fits comfortably.
  function slim(p) {
    return {
      handle: p.handle,
      title: p.title,
      price: p.price,
      available: p.available,
      tags: p.tags || [],
      featured_image: p.featured_image || '',
      options: (p.options || []).map(function (o, i) {
        if (typeof o === 'string') return { name: o, position: i + 1, values: [] };
        return { name: o.name, position: o.position || (i + 1), values: o.values || [] };
      }),
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id,
          option1: v.option1,
          option2: v.option2,
          option3: v.option3,
          available: v.available,
          price: v.price,
          featured_image: v.featured_image ? { src: v.featured_image.src } : null
        };
      })
    };
  }

  function get(handle) {
    var e = readAll().items[handle];
    return e ? e.p : null;
  }

  function fresh(handle) {
    var e = readAll().items[handle];
    return !!e && (Date.now() - e.t) < TTL;
  }

  function put(handle, product) {
    var store = readAll();
    store.items[handle] = { t: Date.now(), p: slim(product) };
    writeAll(store);
  }

  function drop(handle) {
    var store = readAll();
    delete store.items[handle];
    writeAll(store);
  }

  // Fetch and cache anything missing or stale. Failures are ignored here — the
  // page renderer handles real errors when it actually needs the data.
  var warming = {};
  function warm() {
    var list = (window.MDWishlist && window.MDWishlist.get()) || [];
    list.forEach(function (handle) {
      if (fresh(handle) || warming[handle]) return;
      warming[handle] = true;
      fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { if (!r.ok) throw 0; return r.json(); })
        .then(function (p) { put(handle, p); })
        .catch(function () {})
        .then(function () { delete warming[handle]; });
    });
  }

  var warmTimer = null;
  function warmSoon() {
    if (warmTimer) return;
    warmTimer = setTimeout(function () { warmTimer = null; warm(); }, 350);
  }

  /* ------------------------------------------------------ intent prefetch */

  var pagePrefetched = false;

  function prefetchPage() {
    if (pagePrefetched) return;
    pagePrefetched = true;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.as = 'document';
    l.href = PAGE;
    document.head.appendChild(l);
  }

  function isWishlistLink(node) {
    if (!node || !node.closest) return false;
    return !!node.closest('a[href*="page-wishlist"], .header__wishlist-link');
  }

  function onIntent(event) {
    if (!isWishlistLink(event.target)) return;
    prefetchPage();
    warm();
  }

  // mouseover rather than mouseenter, because mouseenter does not bubble.
  ['mouseover', 'touchstart', 'focusin'].forEach(function (ev) {
    document.addEventListener(ev, onIntent, { capture: true, passive: true });
  });

  // Saving a new item warms it straight away, so the wishlist is already
  // populated before the shopper ever opens it.
  document.addEventListener('wishlist:change', warmSoon);

  function boot() {
    // Warm on idle so the first wishlist open is instant even without a hover.
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
    else setTimeout(warm, 1200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.MDWishlistCache = {
    get: get,
    fresh: fresh,
    put: put,
    drop: drop,
    warm: warm,
    prefetchPage: prefetchPage
  };
})();
